class ProductContentsDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerClass = 'wt-product-contents-drawer';
    this.drawer = this;
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = 'wt-product-contents-drawer-overlay';
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      '.wt-product-contents-trigger',
      '.wt-product-contents-drawer__close',
      `.${this.pageOverlayClass}`,
    ].join(', ');

    this.isOpen = false;
    this.mainTrigger = null;
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.ensureOverlayExists();

    // Move to body after a microtask to ensure DOM is ready
    requestAnimationFrame(() => {
      this.moveToBody();
      this.init();
    });
  }

  moveToBody() {
    // Move this drawer element to body level to avoid positioning issues
    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  ensureOverlayExists() {
    if (!document.querySelector(`.${this.pageOverlayClass}`)) {
      const overlay = document.createElement('div');
      overlay.className = this.pageOverlayClass;
      document.body.appendChild(overlay);
    }
  }

  getFocusableElements() {
    const focusableSelector = 'button, [href], input, select, [tabindex]';
    const focusableElements = Array.from(this.querySelectorAll(focusableSelector)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex >= 0,
    );

    return {
      first: focusableElements[0],
      last: focusableElements[focusableElements.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add('no-focus-visible');
    setTimeout(() => {
      document.body.classList.remove('no-focus-visible');
    }, 200);
  }

  onToggle() {
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
      this.isOpen = false;
      if (this.mainTrigger) this.mainTrigger.focus();
    } else {
      this.setAttribute('open', '');
      this.isOpen = true;
      const closeBtn = this.querySelector('.wt-product-contents-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
    this.temporaryHideFocusVisible();
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);

    // Check if any drawer is open
    const anyDrawerOpen = document.querySelector('.wt-product-contents-drawer--open');
    if (anyDrawerOpen) {
      this.body.classList.add(this.activeOverlayBodyClass);
    } else {
      this.body.classList.remove(this.activeOverlayBodyClass);
    }
  }

  init() {
    const productId = this.dataset.productId;
    this.overlay = document.querySelector(`.${this.pageOverlayClass}`);

    // Use a single event handler per drawer instance
    this.clickHandler = (e) => {
      const target = e.target;
      const closestTrigger = target.closest(this.triggerQuery);

      if (closestTrigger) {
        // Check if this trigger is for this specific drawer
        const triggerProductId = closestTrigger.dataset.productId;

        // If it's a trigger button, only respond to our product ID
        if (target.classList.contains('wt-product-contents-trigger')) {
          if (triggerProductId !== productId) {
            return; // Not for this drawer
          }
          e.preventDefault();
          this.mainTrigger = target;
          this.toggleDrawerClasses();
          return;
        }

        // If it's a close button, check if it's ours
        if (target.closest('.wt-product-contents-drawer__close')) {
          if (triggerProductId !== productId) {
            return; // Not our close button
          }
          e.preventDefault();
          this.toggleDrawerClasses();
          return;
        }

        // If it's the overlay, only close if we're open
        if (target.classList.contains(this.pageOverlayClass)) {
          if (this.isOpen) {
            e.preventDefault();
            this.toggleDrawerClasses();
          }
          return;
        }
      }
    };

    document.addEventListener('click', this.clickHandler);

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;

      const { first, last } = this.getFocusableElements();

      // Tab trap
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });
  }

  disconnectedCallback() {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
    }
  }
}

customElements.define('product-contents-drawer', ProductContentsDrawer);
